import { ContentLink, DealerFilterData } from '@api/types';
import {
  BlockContainer,
  DealerListRow,
  DealerListSearch,
  Table,
  TablePagination,
  Title,
} from '@components';
import { useAppDispatch, useDealerList } from '@hooks';
import { Box, styled } from '@mui/material';
import { initBlock } from '@store/table/table.slice';
import { useEffect, useMemo } from 'react';
import { getCustomerColumns } from './DealerListBlock.config';

//CONSTANTS
const DEFAULT_ITEMS_PER_PAGE = 20;

// STYLES
//--------
const DealerListBlockWrapper = styled(Box)``;

// TYPES
//-------
type DealerListBlockProps = {
  title?: string;
  contentLink?: ContentLink;
  numberItemsOnPage?: number;
  visibleFilters: string;
  columnsDisplayed?: string;
  filterBarVisible?: boolean;
};

// COMPONENTS
//------------
export const DealerListBlock = ({
  title,
  contentLink,
  numberItemsOnPage,
  visibleFilters,
  columnsDisplayed,
  filterBarVisible,
}: DealerListBlockProps) => {
  const itemsPerPage =
    numberItemsOnPage && numberItemsOnPage > 0 ? numberItemsOnPage : DEFAULT_ITEMS_PER_PAGE;
  const id = contentLink?.id?.toString() || '';
  const { fetchDealers, response, offset, filters } = useDealerList({ id, itemsPerPage });
  const { isFetching, isLoading, isError, currentData } = response;
  const dispatch = useAppDispatch();
  const totalCount = currentData?.data?.totalCount || 0;
  const isWorking = isLoading || isFetching;
  const visibleColumnNames = columnsDisplayed?.split(',').map((x) => x.trim().toLowerCase());
  const isFiltersVisible = filterBarVisible && visibleFilters;
  const filteredColumns = useMemo(
    () =>
      getCustomerColumns()?.filter((column) =>
        visibleColumnNames?.includes(column.key.toLowerCase())
      ),
    [visibleColumnNames]
  );

  useEffect(() => {
    fetchDealers();
    const visibleFiltersArray = visibleFilters?.split(',') || [];
    const initialFilters: DealerFilterData = {
      Search: '',
    };
    dispatch(initBlock({ id, filters: initialFilters, visibleFilters: visibleFiltersArray }));
  }, [dispatch, fetchDealers, id, visibleFilters]);

  return (
    <BlockContainer>
      <DealerListBlockWrapper className='dealer-list-block'>
        <Box sx={{ mb: 4 }}>
          <Title variant='h2'>{title || 'Dealers'}</Title>
        </Box>
        {isFiltersVisible ? <DealerListSearch id={id} /> : null}
        <Table
          headerColumns={filteredColumns}
          isError={isError}
          isWorking={isFetching || isLoading}
          itemsOnPage={currentData?.data?.customersListFound?.length || itemsPerPage}
          list={currentData?.data?.customersListFound}
          renderRow={(dealer) => (
            <DealerListRow
              key={dealer.id}
              dealer={dealer}
              filters={filters as DealerFilterData}
              isWorking={isWorking}
              visibleColumnNames={visibleColumnNames}
              filteredColumns={filteredColumns}
            />
          )}
        />
        <TablePagination
          id={id}
          start={offset}
          totalCount={totalCount}
          itemsPerPage={itemsPerPage}
          isWorking={isFetching}
        />
      </DealerListBlockWrapper>
    </BlockContainer>
  );
};
